import * as R from 'ramda';
import React from 'react';
const DEFAULT_PRIORITY = 100;
// --- Helper Function ---
/**
 * Default basic cell renderer. Renders primitive values or a comma-separated list for arrays.
 * Handles null/undefined safely. Objects might not render meaningfully without specific Cell renderers.
 * @template Data - The type of the row data.
 * @param {CellProps<Data>} props - Cell properties provided by react-table.
 * @returns {ReactNode} The rendered cell content or null.
 */
const defaultBaseCellRenderer = ({ cell: { value } }) => {
    if (value === null || typeof value === 'undefined') {
        return null;
    }
    if (Array.isArray(value)) {
        return value.map(String).join(', '); // Simple comma-separated string for arrays
    }
    // React handles primitives. Objects might need a specific renderer.
    return value;
};
/**
 * Evaluates decorator rules and aggregates the results for a given context (row or cell).
 * Applies rules based on target, condition, and priority. Merges styles, classes,
 * attributes, and collects all applicable wrapper configurations.
 *
 * @template Data - The type of the row data.
 * @param {ConditionContext<Data>} context - The context (row, cell, tableInstance) for evaluation.
 * @param {DecoratorRule<Data>[]} [decorators=[]] - The array of decorator rules.
 * @param {'row' | 'cell'} target - The target element type ('row' or 'cell').
 * @returns {AggregatedDecorations} The aggregated styles, className, attributes, and an array of wrapper configs.
 */
function evaluateAndAggregateDecorations(context, decorators = [], target) {
    // Filter rules applicable to the current target and context
    const applicableRules = decorators.filter((rule) => {
        var _a;
        // ... (filtering logic remains the same) ...
        if (rule.target !== target)
            return false;
        if (target === 'cell' && (!rule.columnId || rule.columnId !== ((_a = context.cell) === null || _a === void 0 ? void 0 : _a.column.id))) {
            return false;
        }
        try {
            return rule.condition(context);
        }
        catch (e) {
            console.error(`[useDecorators] Error evaluating decorator condition (id: ${rule.id}):`, e);
            return false;
        }
    });
    // Sort rules by priority (lower number = higher priority)
    // Rules with the same priority maintain their original relative order (stable sort behavior if supported)
    applicableRules.sort((a, b) => { var _a, _b; return ((_a = a.priority) !== null && _a !== void 0 ? _a : DEFAULT_PRIORITY) - ((_b = b.priority) !== null && _b !== void 0 ? _b : DEFAULT_PRIORITY); });
    // Initialize aggregated decorations object
    const aggregated = {
        style: {},
        className: '',
        attributes: {},
        wrapperConfigs: [] // Initialize as an empty array
    };
    // Apply decorations from sorted rules
    for (const rule of applicableRules) {
        try {
            // Process each decoration in the array
            for (const decoration of rule.decorations) {
                switch (decoration.type) {
                    case 'style':
                        aggregated.style = R.mergeDeepRight(aggregated.style, decoration.style || {});
                        break;
                    case 'className':
                        aggregated.className = `${aggregated.className} ${decoration.className || ''}`.trim();
                        break;
                    case 'attribute':
                        aggregated.attributes = R.mergeRight(aggregated.attributes, decoration.attributes || {});
                        break;
                    case 'wrapper':
                        // Add all applicable wrapper decorations to the array
                        aggregated.wrapperConfigs.push(decoration);
                        break;
                }
            }
        }
        catch (e) {
            console.error(`[useDecorators] Error applying decorations (id: ${rule.id}):`, e);
        }
    }
    // The wrapperConfigs array is now sorted by priority (lowest priority first)
    return aggregated;
}
/**
 * React Table v7 Plugin Hook.
 * Applies decorators (styles, classes, attributes, wrappers) to rows and cells based on defined rules.
 * Rules are provided via the `decorators` array on the `TableInstance`.
 * It modifies row/cell props via `getRowProps`/`getCellProps` and wraps cell content via `allColumns`.
 *
 * @template Data - The type of the row data. Expected to be an object.
 * @param {Hooks<Data>} hooks - The react-table hooks object.
 */
export const useDecorators = (hooks) => {
    // 1. Modify Row Props (styles, className, attributes)
    hooks.getRowProps.push(((props, meta) => {
        const { instance, row } = meta;
        const decorators = instance.decorators; // Access directly from instance
        const context = { row, tableInstance: instance };
        const aggregated = evaluateAndAggregateDecorations(context, decorators, 'row');
        // Prepare calculated props based on aggregated decorations
        const calculatedProps = {
            ...aggregated.attributes,
            style: { ...props.style, ...aggregated.style }, // Merge styles
            className: `${props.className || ''} ${aggregated.className}`.trim() // Merge classNames
        };
        // Clean up empty values
        if (!calculatedProps.className)
            delete calculatedProps.className;
        if (R.isEmpty(calculatedProps.style) || Object.keys(calculatedProps.style).length === 0)
            delete calculatedProps.style;
        // Return original and calculated props for merging
        return [props, calculatedProps];
    }));
    // 2. Modify Cell Props (styles, className, attributes for the container TD/DIV)
    hooks.getCellProps.push(((props, meta) => {
        const { instance, cell } = meta;
        const decorators = instance.decorators; // Access directly from instance
        const context = { row: cell.row, cell, tableInstance: instance };
        const aggregated = evaluateAndAggregateDecorations(context, decorators, 'cell');
        // Prepare calculated props based on aggregated decorations
        const calculatedProps = {
            ...aggregated.attributes,
            style: { ...props.style, ...aggregated.style }, // Merge styles
            className: `${props.className || ''} ${aggregated.className}`.trim() // Merge classNames
        };
        // Clean up empty values
        if (!calculatedProps.className)
            delete calculatedProps.className;
        if (R.isEmpty(calculatedProps.style) || Object.keys(calculatedProps.style).length === 0)
            delete calculatedProps.style;
        // Return original and calculated props for merging
        return [props, calculatedProps];
    }));
    // 3. Modify Cell Renderer (for content wrapping and ensuring base render)
    hooks.visibleColumns.push((allColumns, meta) => {
        const { instance } = meta;
        const decorators = instance.decorators;
        // Optimization: Skip modification if no decorators are provided
        if (!decorators || R.isEmpty(decorators)) {
            return allColumns;
        }
        allColumns.forEach((column) => {
            // Skip column groups, react-table handles sub-columns recursively
            if (column.columns) {
                return;
            }
            // Store the original renderer only once to prevent infinite recursion and ensure base render
            if (!column.__originalCellDecorator) {
                let rendererToStore;
                if (column.Cell) {
                    rendererToStore = column.Cell; // Use explicitly defined Cell
                }
                else if (column.accessor) {
                    rendererToStore = defaultBaseCellRenderer; // Use base renderer if only accessor exists
                }
                else {
                    rendererToStore = () => null; // Render nothing if no Cell or accessor
                }
                column.__originalCellDecorator = rendererToStore;
            }
            // Retrieve the stored original renderer
            const originalCellRenderer = column.__originalCellDecorator;
            // Override the column's Cell renderer function
            column.Cell = (cellProps) => {
                const context = {
                    row: cellProps.row,
                    cell: cellProps.cell,
                    tableInstance: instance
                };
                // Evaluate rules for this specific cell to find the highest priority wrapper
                const aggregated = evaluateAndAggregateDecorations(context, decorators, 'cell');
                let renderedContent; // Allow ReactElement or other nodes
                try {
                    // Render the original content using the determined original/base renderer
                    renderedContent = originalCellRenderer(cellProps);
                }
                catch (e) {
                    console.error(`[useDecorators] Error executing original cell renderer for column ${column.id}:`, e);
                    // Provide a safe fallback, perhaps an empty fragment or error indicator
                    renderedContent = React.createElement(React.Fragment, null);
                }
                // Apply wrappers if any exist, using reduceRight for correct nesting
                if (aggregated.wrapperConfigs && aggregated.wrapperConfigs.length > 0) {
                    // reduceRight iterates from the last element (highest priority) first
                    const wrappedContent = aggregated.wrapperConfigs.reduceRight((currentContent, wrapperConfig) => {
                        // Inner content becomes the accumulated result of previous (higher priority) wrappers
                        const Wrapper = wrapperConfig.component;
                        const wrapperSpecificProps = wrapperConfig.props;
                        try {
                            // Wrap the current content with the next wrapper
                            return (React.createElement(Wrapper, { cell: cellProps.cell, row: cellProps.row, wrapperProps: wrapperSpecificProps }, currentContent));
                        }
                        catch (e) {
                            console.error(`[useDecorators] Error executing wrapper component for rule ${wrapperConfig.type} on column ${column.id}:`, e);
                            // If a wrapper fails, return the content as it was before this wrapper
                            return currentContent;
                        }
                    }, renderedContent // Initial value for the reduction is the original rendered content
                    );
                    return wrappedContent; // Return the fully wrapped content
                }
                // Return the (potentially base-rendered) content if no wrapper applies
                return renderedContent;
            };
        });
        return allColumns; // Return the array of modified column instances
    });
};
// Set the plugin name for react-table discovery/debugging
useDecorators.pluginName = 'useDecorators';
